import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminSupportingText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import { isTeamOwnerActor, isTeamCoOwnerActor } from '../../../data/teamConfig';
import {
  CONDITIONS_STATEMENT,
  DRAFT_POST_BEHAVIOUR,
  GRACE_ELIGIBILITY,
  MINIMUM_PRICE_NOTICE_LABEL,
  MINIMUM_PRICE_NOTICE_TABLE_LABEL,
  PAYMENT_METHOD_DURING_TRIAL,
  REACTIVATION_PAYMENT,
  AUTOMATIC_POST_RESTORATION,
  buildPricingPlanListRows,
  buildSamplePricingPlanConfigurations,
  enrichConfigurationRecords,
  formatCurrentCostDisplay,
  formatDaysLabel,
  formatEffectiveFromOrDash,
  formatPendingCostDisplay,
  formatPlanCell,
  formatPublicFeaturePreview,
  getQuoteAllowanceQualificationNote,
  labelPlanChangeForTable,
  labelTrialForTable,
  labelVisibilityForTable,
} from '../../../data/plansConfig';
import {
  buildSamplePricingPlanManagementRecords,
  formatAuditDisplay,
  formatGstDisplay,
} from '../../../data/pricingPlanManagementData';

const tableCols =
  'minmax(72px, 0.55fr) minmax(100px, 0.8fr) minmax(68px, 0.48fr) minmax(52px, 0.36fr) minmax(64px, 0.42fr) minmax(56px, 0.4fr) minmax(72px, 0.5fr) minmax(72px, 0.5fr) minmax(56px, 0.4fr) minmax(110px, 0.85fr) minmax(72px, 0.5fr) minmax(48px, 0.2fr)';

const TABLE_MIN_WIDTH = '1280px';

const canAccessPricingPlanConfiguration = (auth) =>
  isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);

const SummaryField = ({ label, value, mono = false, multiline = false }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {multiline ? (
      <AdminDetailFieldValue className="break-words whitespace-pre-wrap">
        {value || '—'}
      </AdminDetailFieldValue>
    ) : mono ? (
      <p className="text-offblack text-sm py-1 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="break-words">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

function PlanConfigViewPopup({ row, managementPlan, onClose }) {
  if (!row) return null;

  const planKey = row.planKey;
  const showTrialLength = planKey === 'basic' && row.trialIncluded === 'Yes';
  const featurePreview = formatPublicFeaturePreview(row);
  const quoteNote = getQuoteAllowanceQualificationNote(row);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-plan-config-view-title"
      >
        <div className="relative px-5 pt-5 pb-3 border-b border-lightgray">
          <AdminPanelTitle
            id="pricing-plan-config-view-title"
            className="text-offblack text-center"
          >
            Pricing Plan Configuration Summary
          </AdminPanelTitle>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 text-offblack hover:text-primaryblue"
            aria-label="Close"
          >
            <i className="fi fi-rs-cross text-base" aria-hidden />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <SummaryField label="Config ID" value={row.id} mono />
          <SummaryField
            label="Source Plan"
            value={formatPlanCell(row.planId, row.planName)}
          />
          <SummaryField label="Conditions" value={CONDITIONS_STATEMENT} />

          {managementPlan ? (
            <>
              <SummaryField label="Plan ID" value={managementPlan.id} mono />
              <SummaryField label="Plan Name" value={managementPlan.planName} />
              <SummaryField
                label="Current Monthly Cost"
                value={formatCurrentCostDisplay(managementPlan)}
              />
              <SummaryField
                label="Pending Monthly Cost"
                value={formatPendingCostDisplay(managementPlan)}
              />
              <SummaryField
                label="Effective From"
                value={formatEffectiveFromOrDash(managementPlan)}
              />
              <SummaryField
                label="GST"
                value={formatGstDisplay(managementPlan.gstIncluded)}
              />
              <SummaryField
                label="Billing Cycle"
                value={managementPlan.billingCycle || '—'}
              />
              <SummaryField
                label="Public Description"
                value={managementPlan.publicDescription || '—'}
                multiline
              />
            </>
          ) : null}

          {planKey === 'pro' ? (
            <SummaryField label="Trial Included — Pro" value="No" />
          ) : (
            <>
              <SummaryField label="Trial Included — Basic" value={row.trialIncluded || '—'} />
              {showTrialLength ? (
                <SummaryField
                  label="Basic Trial Length"
                  value={formatDaysLabel(row.trialLengthDays)}
                />
              ) : null}
              {row.trialIncluded === 'Yes' ? (
                <SummaryField
                  label="Payment Method During Trial"
                  value={row.paymentMethodDuringTrial || PAYMENT_METHOD_DURING_TRIAL}
                />
              ) : null}
            </>
          )}

          <SummaryField label="Active Post Allowance" value={row.postsAllowed} />
          <SummaryField label="Active Featured Post Allowance" value={row.featuredPostsAllowed} />
          <SummaryField label="Quotes Allowed" value={row.quotesAllowed} />
          <SummaryField label="Draft Post Behaviour" value={DRAFT_POST_BEHAVIOUR} />
          <SummaryField label="Quote Allowance Note" value={quoteNote} multiline />

          <SummaryField
            label="Contact Details Visibility"
            value={row.contactDetailsVisibility || '—'}
          />
          <SummaryField
            label="Allowed External Links Visibility"
            value={row.allowedExternalLinksVisibility || '—'}
          />

          <SummaryField label="Grace Period" value={formatDaysLabel(row.gracePeriodDays)} />
          <SummaryField label="Grace Eligibility" value={GRACE_ELIGIBILITY} />

          <SummaryField label="Reactivation Payment" value={REACTIVATION_PAYMENT} />
          <SummaryField
            label="Automatic Post Restoration"
            value={AUTOMATIC_POST_RESTORATION}
          />

          {planKey === 'basic' ? (
            <>
              <SummaryField label="Upgrade Timing" value={row.upgradeTiming || '—'} />
              <SummaryField
                label="Paid Basic Upgrade Charge"
                value={row.upgradeChargeMethod || '—'}
              />
            </>
          ) : (
            <SummaryField label="Downgrade Timing" value={row.downgradeTiming || '—'} />
          )}

          <SummaryField label="Cancellation Timing" value={row.cancellationTiming || '—'} />
          <SummaryField label="Public Feature Preview" value={featurePreview} multiline />
          <SummaryField label="Price Change Notice" value={MINIMUM_PRICE_NOTICE_LABEL} />

          <SummaryField label="Created On" value={formatAuditDisplay(row.createdOn)} />
          <SummaryField label="Created By" value={row.createdBy || '—'} />
          <SummaryField
            label="Last Updated On"
            value={formatAuditDisplay(row.lastUpdatedOn || row.lastConfirmedOn)}
          />
          <SummaryField
            label="Last Updated By"
            value={row.lastUpdatedBy || row.lastConfirmedBy || '—'}
          />
        </div>

        <div className="px-5 pb-5 flex justify-end">
          <AdminFormSecondaryAction type="button" onClick={onClose}>
            Close
          </AdminFormSecondaryAction>
        </div>
      </div>
    </div>
  );
}

const Plans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const canAccess = canAccessPricingPlanConfiguration(auth);

  const [managementPlans, setManagementPlans] = useState(() =>
    buildSamplePricingPlanManagementRecords(),
  );
  const [configurations, setConfigurations] = useState(() =>
    buildSamplePricingPlanConfigurations(buildSamplePricingPlanManagementRecords()),
  );
  const [saveSuccess, setSaveSuccess] = useState(location.state?.saveSuccess || '');
  const [viewRow, setViewRow] = useState(null);

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  useEffect(() => {
    const incomingConfigs = location.state?.configurations;
    const incomingManagement = location.state?.pricingPlans;
    if (!incomingConfigs && !incomingManagement && !location.state?.saveSuccess) return;

    if (Array.isArray(incomingManagement)) {
      setManagementPlans(incomingManagement);
    }
    if (Array.isArray(incomingConfigs)) {
      setConfigurations(
        enrichConfigurationRecords(
          incomingConfigs,
          Array.isArray(incomingManagement) ? incomingManagement : undefined,
        ),
      );
    }
    if (location.state?.saveSuccess) {
      setSaveSuccess(location.state.saveSuccess);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const listRows = useMemo(
    () => buildPricingPlanListRows(managementPlans, configurations),
    [managementPlans, configurations],
  );

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRows,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(listRows);

  const sharedState = { configurations, pricingPlans: managementPlans };

  const openAdd = () => {
    navigate('/admin/configuration/plans/add', { state: sharedState });
  };

  const openPlanEdit = (row) => {
    navigate(`/admin/configuration/plans/${encodeURIComponent(row.id)}/edit`, {
      state: { configuration: row, ...sharedState },
    });
  };

  const openView = (row) => setViewRow(row);

  const viewManagementPlan = viewRow
    ? managementPlans.find((plan) => plan.id === viewRow.planId) || null
    : null;

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Pricing Plans</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to view Pricing Plans. Access is limited to Owner and
          Co-owner.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-w-0 max-w-full overflow-hidden">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Pricing Plans</AdminPageTitle>
          <AdminHeaderAction type="button" onClick={openAdd}>
            Add Rule
          </AdminHeaderAction>
        </div>

        {saveSuccess ? (
          <AdminSupportingText className="text-freshgreen mb-4">{saveSuccess}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0 overflow-hidden`}>
          <div ref={tableScrollRef} className="overflow-x-auto min-w-0">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-2 gap-y-2"
              style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
            >
              {[
                'Config ID',
                'Plan',
                'Trial',
                'Posts',
                'Featured',
                'Quotes',
                'Contact',
                'Links',
                'Grace',
                'Plan Change',
                'Notice',
                'Actions',
              ].map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0${label === 'Actions' ? ' shrink-0 justify-center' : ''}`}
                >
                  <AdminTableHeader className="truncate">{label}</AdminTableHeader>
                </div>
              ))}
            </div>

            {paginatedRows.map((row) => (
              <div
                key={row.id}
                className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 gap-y-2"
                style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
              >
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableMeta className="truncate block" title={row.id}>
                    {row.id}
                  </AdminTableMeta>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody
                    className="truncate block"
                    title={formatPlanCell(row.planId, row.planName)}
                  >
                    {formatPlanCell(row.planId, row.planName)}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody className="truncate block">{labelTrialForTable(row)}</AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody className="truncate block">{row.postsAllowed}</AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody className="truncate block">{row.featuredPostsAllowed}</AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody className="truncate block">{row.quotesAllowed}</AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody
                    className="truncate block"
                    title={row.contactDetailsVisibility}
                  >
                    {labelVisibilityForTable(row.contactDetailsVisibility)}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody
                    className="truncate block"
                    title={row.allowedExternalLinksVisibility}
                  >
                    {labelVisibilityForTable(row.allowedExternalLinksVisibility)}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody className="truncate block">
                    {formatDaysLabel(row.gracePeriodDays)}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody className="truncate block">
                    {labelPlanChangeForTable(row)}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 overflow-hidden">
                  <AdminTableBody className="truncate block" title={MINIMUM_PRICE_NOTICE_LABEL}>
                    {MINIMUM_PRICE_NOTICE_TABLE_LABEL}
                  </AdminTableBody>
                </div>
                <div className="flex items-center justify-center shrink-0 overflow-visible">
                  <div
                    className="relative inline-flex"
                    ref={(el) => {
                      rowActionsRefs.current[row.id] = el;
                    }}
                  >
                    <AdminRowActionsButton
                      onClick={() => toggleRowActions(row.id)}
                      aria-expanded={!!rowActionsOpen[row.id]}
                      title="Actions"
                      aria-label="Row actions"
                    >
                      <AdminRowMenuIcon />
                    </AdminRowActionsButton>
                    {rowActionsOpen[row.id] && rowActionsMenuLayout ? (
                      <AdminFixedRowActionsMenu
                        layout={rowActionsMenuLayout}
                        menuRef={rowActionsMenuRef}
                        placementReady={rowActionsMenuPlacementReady}
                      >
                        <AdminRowMenuItem
                          onClick={() => {
                            closeRowActions();
                            openView(row);
                          }}
                        >
                          View
                        </AdminRowMenuItem>
                        <AdminRowMenuItem
                          onClick={() => {
                            closeRowActions();
                            openPlanEdit(row);
                          }}
                        >
                          Edit
                        </AdminRowMenuItem>
                      </AdminFixedRowActionsMenu>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>

      {viewRow ? (
        <PlanConfigViewPopup
          row={viewRow}
          managementPlan={viewManagementPlan}
          onClose={() => setViewRow(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default Plans;
